import { useRef, useState } from 'react';

type MicState = 'idle' | 'recording' | 'working';

/**
 * Tap to record a short voice clip → Whisper (/api/ai/transcribe) → onResult(text).
 * Uses MediaRecorder; silently no-ops where the mic API is unavailable. Callers
 * always keep a text input as the fallback.
 */
export function MicButton({
  onResult,
  title = 'Speak instead of typing',
}: {
  onResult: (text: string) => void;
  title?: string;
}) {
  const [state, setState] = useState<MicState>('idle');
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const supported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';
  if (!supported) return null;

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState('working');
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        const fd = new FormData();
        fd.append('audio', blob, 'clip.webm');
        try {
          const res = await fetch('/api/ai/transcribe', {
            method: 'POST',
            body: fd,
            credentials: 'include',
          });
          const d = (await res.json().catch(() => null)) as {
            ok: boolean;
            data?: { text: string };
          } | null;
          if (d?.ok && d.data?.text) onResult(d.data.text);
        } finally {
          setState('idle');
        }
      };
      recRef.current = rec;
      rec.start();
      setState('recording');
    } catch {
      setState('idle');
    }
  }

  function stop() {
    recRef.current?.stop();
  }

  return (
    <button
      type="button"
      className={`mic-btn ${state !== 'idle' ? 'is-active' : ''}`}
      onClick={state === 'recording' ? stop : state === 'idle' ? start : undefined}
      disabled={state === 'working'}
      aria-label={state === 'recording' ? 'Stop recording' : title}
      title={state === 'recording' ? 'Stop recording' : title}
    >
      {state === 'working' ? '…' : state === 'recording' ? '■' : '🎤'}
    </button>
  );
}
